/* eslint-disable @typescript-eslint/no-explicit-any */
import { AISolutionId, ICounts, IDataSource } from '../common/types/common';
import { IOrganization } from '../common/types/organisation';
import { API_BASE, API_GRAPH_BASE, API_LLM_BASE } from '../config/env';
import { httpConstants } from '../constants';
import { EnterpriseContextState } from '../features/EnterpriseContext/EnterpriseContextInterfaces';
import { NewSolution } from '../features/NewSolutionXPLR/SolutionXPLRInterfaces';
import { httpService } from '../utility/httpService';

// const API_BASE = import.meta.env.VITE_ADMIN_URL;

export const getOrganisations = async (): Promise<any> => {
  let organisations: any = null;
  const url = `${API_GRAPH_BASE}/organizations`;
  organisations = await httpService<IOrganization[]>(
    httpConstants.METHOD_TYPE.GET,
    url,
    undefined,
    undefined
  );
  return organisations;
};

// Lightweight organizations list (id, name, summary only)
export const getOrganisationsLite = async (): Promise<IOrganization[]> => {
  const url = `${API_GRAPH_BASE}/organizations-lite`;
  return httpService<IOrganization[]>(httpConstants.METHOD_TYPE.GET, url, undefined, undefined);
};

// Fetch a single organization with full taxonomy/customTaxonomy payload
export const getOrganizationById = async (organizationId: string): Promise<IOrganization> => {
  const url = `${API_GRAPH_BASE}/organizations/${organizationId}`;
  return httpService<IOrganization>(httpConstants.METHOD_TYPE.GET, url, undefined, undefined);
};

// Persist last-used organization for the current user
export const updateLastOrganization = async (organizationId: string): Promise<void> => {
  const url = `${API_GRAPH_BASE}/users/me/last-organization`;
  await httpService<void>(
    httpConstants.METHOD_TYPE.PATCH,
    url,
    { organization_id: organizationId },
    undefined
  );
};

export const patchEnterpriseContext = async (
  data: { description?: string },
  organizationId?: string
): Promise<any> => {
  const url = `${API_GRAPH_BASE}/enterprise-technology/organization/${organizationId}/save-industry`;
  return httpService<EnterpriseContextState>(httpConstants.METHOD_TYPE.PATCH, url, data, undefined);
};

// New API for saving technology landscape
export const saveTechnologyLandscape = async (
  payload: any,
  organizationId: string
): Promise<any> => {
  const url = `${API_GRAPH_BASE}/enterprise-technology/organization/${organizationId}/save-industry`;
  const response = await httpService<any>(httpConstants.METHOD_TYPE.PATCH, url, payload, undefined);
  return response;
};

// New API for getting enterprise technology landscape
export const getEnterpriseTechnology = async (organizationId: string): Promise<any> => {
  const url = `${API_GRAPH_BASE}/enterprise-technology/organization/${organizationId}`;
  const response = await httpService<any>(httpConstants.METHOD_TYPE.GET, url, undefined, undefined);
  return response;
};

export const getCounts = async (
  orgId: string,
  industries: string,
  categories: string,
  valueDriversScore?: string,
  impactAssessment?: string
): Promise<any> => {
  let counts: any = null;
  let url = `${API_BASE}/api/v1/count/?organizationId=${orgId}`;

  if (industries) {
    url += `&industries=${industries}`;
  }

  if (categories) {
    url += `&categories=${categories}`;
  }

  if (valueDriversScore) {
    url += `&valueDriversScore=${valueDriversScore}`;
  }

  if (impactAssessment) {
    url += `&impactAssessment=${impactAssessment}`;
  }

  counts = await httpService<ICounts>(httpConstants.METHOD_TYPE.GET, url, undefined, undefined);
  return counts;
};

// New AI Count API service function
export const getAICount = async (
  organizationId: string,
  limit: number,
  offset: number,
  name?: string,
  industries?: string,
  categories?: string,
  valueDriversScore?: string,
  impactAssessment?: string
): Promise<any> => {
  let url = `${API_BASE}/api/v1/ai_count/?organizationId=${organizationId}&limit=${limit}&offset=${offset}`;

  if (name) {
    url += `&name=${encodeURIComponent(name)}`;
  }

  if (industries) {
    url += `&industries=${encodeURIComponent(industries)}`;
  }

  if (categories) {
    url += `&categories=${encodeURIComponent(categories)}`;
  }

  if (valueDriversScore) {
    url += `&valueDriversScore=${encodeURIComponent(valueDriversScore)}`;
  }

  if (impactAssessment) {
    url += `&impactAssessment=${encodeURIComponent(impactAssessment)}`;
  }

  return await httpService<any>(httpConstants.METHOD_TYPE.GET, url, undefined, undefined);
};

export const generateDataSource = async (solution_id: AISolutionId): Promise<any> => {
  const payload = {
    ai_solution_id: solution_id,
  };
  const url = `${API_LLM_BASE}/api/ai/v1/ai-solutions/generate-data-sources-implementation-considerations/`;
  const dataSource = await httpService<IDataSource>(
    httpConstants.METHOD_TYPE.POST,
    url,
    payload,
    undefined
  );
  return dataSource;
};

export const updateSolutionAPI = async (
  solutionId: string,
  payload: Partial<NewSolution>
): Promise<NewSolution> => {
  return await httpService<NewSolution>(
    httpConstants.METHOD_TYPE.PATCH,
    `${API_GRAPH_BASE}/new-solution/${solutionId}`,
    payload
  );
};

export const getIndustryTechnologyComplete = async (
  industryId?: string
): Promise<{ functions: boolean; data: any; content: string } | undefined> => {
  if (!industryId?.length) return;

  const url = `${API_GRAPH_BASE}/technology/industry/id/${industryId}/complete`;

  return httpService<{
    functions: boolean;
    data: any;
    content: string;
  }>(httpConstants.METHOD_TYPE.GET, url);
};

export const getOrganizationTechnologyComplete = async (
  organizationId?: string
): Promise<{ functions: boolean; data: any; content: string } | undefined> => {
  if (!organizationId?.length) return;

  const url = `${API_GRAPH_BASE}/enterprise-technology/organization/${organizationId}`;

  return httpService<{
    functions: boolean;
    data: any;
    content: string;
  }>(httpConstants.METHOD_TYPE.GET, url);
};

export const saveOrganizationIndustry = async (
  organizationId: string,
  selectedIndustry: string
): Promise<any> => {
  const url = `${API_GRAPH_BASE}/enterprise-technology/organization/${organizationId}/save-industry`;

  return httpService<any>(httpConstants.METHOD_TYPE.PATCH, url, {
    selected_industry: selectedIndustry,
  });
};
// New API for creating enterprise technology (matches curl POST /save)
export const createEnterpriseTechnology = async (payload: {
  organization_id: string;
  company_name: string;
  status: string;
  description: string;
  is_active: boolean;
  selected_industry: string;
}): Promise<any> => {
  const url = `${API_GRAPH_BASE}/enterprise-technology/save`;

  return httpService<any>(httpConstants.METHOD_TYPE.POST, url, payload, undefined);
};

export const createNewProcess = async (processName: string, functionId: string): Promise<any> => {
  const organizationId = localStorage.getItem('current_organization');
  if (!organizationId) {
    throw new Error('Organization ID is required');
  }
  const url = `${API_GRAPH_BASE}/custom-process/process`;
  return httpService<any>(httpConstants.METHOD_TYPE.POST, url, {
    function_id: functionId,
    organization_id: organizationId,
    name: processName,
  });
};

export const addProcessInOrganization = async (processIds: string[]): Promise<any> => {
  const organizationId = localStorage.getItem('current_organization');
  if (!organizationId) {
    throw new Error('Organization ID is required');
  }
  const url = `${API_GRAPH_BASE}/custom-process/organization/${organizationId}/custom-taxonomy`;
  return httpService<any>(httpConstants.METHOD_TYPE.PATCH, url, {
    ids: [...processIds],
  });
};

export const getFilterUserOptions = async (): Promise<string[]> => {
  const organizationId = localStorage.getItem('current_organization');
  const url = `${API_GRAPH_BASE}/new-solution/${organizationId}/created-by-options`;
  return httpService<string[]>(httpConstants.METHOD_TYPE.GET, url);
};
